import {
  bigMax,
  bigMin,
  getCustomerMarketDataSnapshotRequestKey,
  SecurityMarketDataCell,
  toBig,
  type Column,
  type CustomerSecurityMarketDataColumnParams,
  type Security,
  type SecurityMarketDataCellPriceActions,
  type SecurityMarketDataCellValue,
} from '@talos/kyoko';
import {
  getMarketDataSnapshotForRequestKey,
  getMarketDataSnapshotSizeBucketLevel,
  toSecurityMarketDataCellValue,
  tryReadSecurityMarketDataRow,
} from '@talos/kyoko/src/components/BlotterTable/columns/securityMarketData';
import type {
  Column as AgColumn,
  ColDef,
  ICellRendererParams,
  ValueGetterFunc,
  ValueGetterParams,
} from 'ag-grid-community';
import type Big from 'big.js';
import { useIsPricingModeBPS } from '../../../hooks';
import { isPriceLadderMode } from '../ladder';
import type { PriceGridEntity } from '../PriceGridEntity';
import { usePriceGridSettingsContext } from '../PriceGridSettingsContext';
import {
  PRICE_GRID_CUSTOMER_MARKET_DATA_COLUMN_TYPE,
  PRICE_GRID_MARKET_DATA_COLUMN_INITIAL_WIDTH,
  PRICE_GRID_MARKET_DATA_COLUMN_TYPE,
} from './constants';
import {
  getPriceGridMarketDataColumnId,
  getSymbolFromRow,
  PRICE_GRID_MARKET_DATA_LEAF_COLUMN_PROPS,
  PRICE_GRID_MARKET_DATA_PRICE_COLUMN_SUFFIX,
  type PriceGridMarketDataColumnRow,
} from './priceGridMarketDataColumnUtils';
import {
  getPriceGridMarketDataCellValueWithLadder,
  type PriceGridLadderColumnConfig,
} from './priceGridMarketDataQtyColumns';
import type { PriceGridSecurityMarketDataColumnParams } from './types';
import { getPriceGridColumnParams } from './utils';

type PriceGridMarketDataValueParams = Pick<
  ValueGetterParams<PriceGridEntity, SecurityMarketDataCellValue>,
  'api' | 'colDef' | 'column' | 'context' | 'data' | 'getValue' | 'node'
>;

function getPriceActions(
  column: Column<PriceGridSecurityMarketDataColumnParams, PriceGridMarketDataColumnRow>,
  params: ICellRendererParams<PriceGridEntity, SecurityMarketDataCellValue>,
  row: PriceGridEntity
): SecurityMarketDataCellPriceActions | undefined {
  const symbol = getSymbolFromRow(column, row);
  return symbol
    ? column.params?.getPriceActions?.({
        params: { ...params, data: row, value: row },
        symbol,
        columnParams: column.params,
      })
    : undefined;
}

type PriceGridSecurityMarketDataCellProps = Pick<
  ICellRendererParams<PriceGridEntity, SecurityMarketDataCellValue>,
  'value'
> & {
  priceActions?: SecurityMarketDataCellPriceActions;
  priceIncrement?: string;
  isPricingModeBPSEligible?: boolean;
  security?: Security;
};

export function PriceGridSecurityMarketDataCell({
  value,
  priceActions,
  priceIncrement,
  isPricingModeBPSEligible = false,
  security,
}: PriceGridSecurityMarketDataCellProps) {
  const { alignPricesOnDecimal } = usePriceGridSettingsContext();
  const isPricingModeBPS = useIsPricingModeBPS(isPricingModeBPSEligible ? security : undefined);

  return (
    <SecurityMarketDataCell
      value={value}
      alignPricesOnDecimal={alignPricesOnDecimal}
      isPricingModeBPS={isPricingModeBPS}
      priceActions={priceActions}
      priceIncrement={priceIncrement}
    />
  );
}

function getPriceGridCustomerMarketDataCellValue(
  column: Column<CustomerSecurityMarketDataColumnParams<PriceGridMarketDataColumnRow>, PriceGridMarketDataColumnRow>,
  node: ValueGetterParams['node']
): SecurityMarketDataCellValue {
  const row = tryReadSecurityMarketDataRow<
    PriceGridMarketDataColumnRow,
    CustomerSecurityMarketDataColumnParams<PriceGridMarketDataColumnRow>
  >(column, node);
  if (!row) {
    return undefined;
  }

  const { data, symbol, marketDataCollectionPath } = row;
  const requestParams = column.params?.getRequestParams?.({
    data,
    symbol,
  });
  if (!requestParams?.Counterparty) {
    return undefined;
  }

  const { Counterparty, ...customerRequestParams } = requestParams;
  const requestKey = getCustomerMarketDataSnapshotRequestKey({
    Symbol: symbol,
    Counterparty,
    ...customerRequestParams,
  });
  const snapshot = getMarketDataSnapshotForRequestKey(data, marketDataCollectionPath, requestKey);
  const sizeBucketLevel = getMarketDataSnapshotSizeBucketLevel(data, snapshot, column.params?.sizeBucketField);
  if (column.params?.sizeBucketField && !sizeBucketLevel) {
    return undefined;
  }
  return toSecurityMarketDataCellValue(snapshot, sizeBucketLevel);
}

export function getPriceGridBBOHighlightState({
  priceColumnCount,
  value,
  values,
}: {
  priceColumnCount?: number;
  value: SecurityMarketDataCellValue;
  values: SecurityMarketDataCellValue[];
}): Pick<NonNullable<SecurityMarketDataCellValue>, 'highlightBid' | 'highlightOffer'> {
  if ((priceColumnCount ?? values.length) < 2) {
    return { highlightBid: false, highlightOffer: false };
  }

  const symbol = value?.marketData.Symbol;
  const comparableValues = values.filter(item => item?.marketData.Symbol === symbol);
  const bidPrices = comparableValues
    .map(item => toBig(item?.bid?.Price))
    .filter((price): price is Big => price != null);
  const offerPrices = comparableValues
    .map(item => toBig(item?.offer?.Price))
    .filter((price): price is Big => price != null);
  const bidPrice = toBig(value?.bid?.Price);
  const offerPrice = toBig(value?.offer?.Price);
  const bestBidPrice = bigMax(...bidPrices);
  const bestOfferPrice = bigMin(...offerPrices);

  return {
    highlightBid: bidPrice != null && bestBidPrice != null && bidPrices.length > 1 && bidPrice.eq(bestBidPrice),
    highlightOffer:
      offerPrice != null && bestOfferPrice != null && offerPrices.length > 1 && offerPrice.eq(bestOfferPrice),
  };
}

function getDisplayedPriceGridMarketDataPriceColumns(params: PriceGridMarketDataValueParams): AgColumn[] {
  return params.api
    .getAllDisplayedColumns()
    .filter(
      column =>
        getPriceGridMarketDataColumnParams(column) != null || getPriceGridCustomerMarketDataColumnValueGetter(column)
    );
}

function getPriceGridMarketDataColumnParams(column: AgColumn) {
  if (!column.getColId().endsWith(`-${PRICE_GRID_MARKET_DATA_PRICE_COLUMN_SUFFIX}`)) {
    return undefined;
  }

  return getPriceGridColumnParams<PriceGridSecurityMarketDataColumnParams>(column, PRICE_GRID_MARKET_DATA_COLUMN_TYPE);
}

function getPriceGridCustomerMarketDataColumnValueGetter(
  column: AgColumn
): ValueGetterFunc<PriceGridEntity, SecurityMarketDataCellValue> | undefined {
  if (!getPriceGridColumnParams(column, PRICE_GRID_CUSTOMER_MARKET_DATA_COLUMN_TYPE)) {
    return undefined;
  }

  const { valueGetter } = column.getColDef();
  return typeof valueGetter === 'function'
    ? (valueGetter as ValueGetterFunc<PriceGridEntity, SecurityMarketDataCellValue>)
    : undefined;
}

function getPriceGridDisplayedPriceColumnValue({
  column,
  ladderConfig,
  params,
}: {
  column: AgColumn;
  ladderConfig: PriceGridLadderColumnConfig;
  params: PriceGridMarketDataValueParams;
}) {
  const priceColumnParams = getPriceGridMarketDataColumnParams(column);
  if (priceColumnParams) {
    return getPriceGridMarketDataCellValueWithLadder({
      columnParams: priceColumnParams,
      ladderConfig,
      params: { ...params, column },
    });
  }

  return (
    getPriceGridCustomerMarketDataColumnValueGetter(column)?.({
      ...params,
      colDef: column.getColDef(),
      column,
    } as ValueGetterParams<PriceGridEntity, SecurityMarketDataCellValue>) ?? undefined
  );
}

export function getPriceGridMarketDataCellValueWithBBO({
  columnParams,
  isBBOEnabled,
  ladderConfig,
  params,
}: {
  columnParams: PriceGridSecurityMarketDataColumnParams;
  isBBOEnabled: boolean;
  ladderConfig: PriceGridLadderColumnConfig;
  params: PriceGridMarketDataValueParams;
}): SecurityMarketDataCellValue {
  const value = getPriceGridMarketDataCellValueWithLadder({ columnParams, params, ladderConfig });
  if (!isBBOEnabled || !value) {
    return value;
  }

  const priceColumns = getDisplayedPriceGridMarketDataPriceColumns(params);
  if (priceColumns.length < 2) {
    return { ...value, highlightBid: false, highlightOffer: false };
  }

  // BBO is derived from the visible price cells only, so hidden market-data snapshots never affect highlighted state.
  const values = priceColumns.map(column => getPriceGridDisplayedPriceColumnValue({ column, ladderConfig, params }));

  return {
    ...value,
    ...getPriceGridBBOHighlightState({ priceColumnCount: priceColumns.length, value, values }),
  };
}

export function getPriceGridMarketDataPriceColumn(
  column: Column<PriceGridSecurityMarketDataColumnParams, PriceGridMarketDataColumnRow>,
  { isBBOEnabled, ladderConfig }: { isBBOEnabled: boolean; ladderConfig: PriceGridLadderColumnConfig }
): Column<ColDef<PriceGridEntity, SecurityMarketDataCellValue>, PriceGridMarketDataColumnRow> {
  const colId = getPriceGridMarketDataColumnId(column, PRICE_GRID_MARKET_DATA_PRICE_COLUMN_SUFFIX);

  return {
    type: 'custom',
    id: colId,
    field: column.field,
    title: '',
    initialWidth: PRICE_GRID_MARKET_DATA_COLUMN_INITIAL_WIDTH,
    dynamics: column.dynamics,
    sortable: false,
    ...PRICE_GRID_MARKET_DATA_LEAF_COLUMN_PROPS,
    params: {
      colId,
      contextMenuItems: [],
      headerName: '',
      cellRendererParams: {
        priceGridMarketDataColumnType: PRICE_GRID_MARKET_DATA_COLUMN_TYPE,
        priceGridMarketDataColumnParams: column.params,
      },
      valueGetter: (params: ValueGetterParams<PriceGridEntity, SecurityMarketDataCellValue>) => {
        if (!column.params) {
          return undefined;
        }
        return getPriceGridMarketDataCellValueWithBBO({
          columnParams: column.params,
          isBBOEnabled,
          ladderConfig,
          params,
        });
      },
      cellRenderer: (params: ICellRendererParams<PriceGridEntity, SecurityMarketDataCellValue>) => {
        const row = params.data;
        if (!row || !column.params) {
          return null;
        }
        return (
          <PriceGridSecurityMarketDataCell
            value={params.value}
            priceActions={getPriceActions(column, params, row)}
            isPricingModeBPSEligible={column.field === 'security.Symbol'}
            security={row.security}
            priceIncrement={isPriceLadderMode(ladderConfig.ladderType) ? ladderConfig.priceIncrement : undefined}
          />
        );
      },
    },
  };
}

export function getPriceGridCustomerMarketDataPriceColumn(
  column: Column<CustomerSecurityMarketDataColumnParams<PriceGridMarketDataColumnRow>, PriceGridMarketDataColumnRow>
): Column<ColDef<PriceGridMarketDataColumnRow, SecurityMarketDataCellValue>, PriceGridMarketDataColumnRow> {
  return {
    type: 'custom',
    id: column.id,
    field: column.field,
    title: column.title,
    initialWidth: column.initialWidth,
    sortable: false,
    suppressColumnsToolPanel: true,
    params: {
      colId: column.id,
      headerName: typeof column.title === 'string' ? column.title : undefined,
      cellRendererParams: {
        priceGridMarketDataColumnType: PRICE_GRID_CUSTOMER_MARKET_DATA_COLUMN_TYPE,
        priceGridMarketDataColumnParams: column.params,
      },
      valueGetter: ({ node }: ValueGetterParams<PriceGridMarketDataColumnRow, SecurityMarketDataCellValue>) =>
        getPriceGridCustomerMarketDataCellValue(column, node),
      cellRenderer: (params: ICellRendererParams<PriceGridMarketDataColumnRow, SecurityMarketDataCellValue>) => (
        <PriceGridSecurityMarketDataCell value={params.value} />
      ),
    },
  };
}

export type { PriceGridLadderColumnConfig };
